export const TEMPLATE_OPTIONS = [
  { value: "required_charge", label: "Required Charge" },
  { value: "quantity_match", label: "Quantity Match" },
  { value: "charge_behavior", label: "Charge Behavior" },
  { value: "underminimum_required_charge", label: "Minimum Quantity Charge" },
  { value: "missing_net_price_or_cost", label: "Missing Net / Client Price" },
  { value: "required_proof", label: "Proof Required" },
  { value: "stitch_threshold", label: "Stitch Threshold Pricing" },
  { value: "dtf_square_inch_pricing", label: "Square Inch Pricing" },
];

export const COMING_SOON_TEMPLATES = [
  {
    id: "blackout_dates",
    label: "Blackout Dates",
    description: "Flag decorator requests that land on configured closed or unavailable dates.",
  },
  {
    id: "order_grouping_warning",
    label: "Order Grouping Warning",
    description: "Warn when grouped work should stay separated, such as different project numbers.",
  },
  {
    id: "combine_logos",
    label: "Combined Logo Pricing",
    description: "Future specialized pricing rule for combined logo scenarios.",
  },
  {
    id: "screen_color_count",
    label: "Screen Color Count",
    description: "Future specialized rule for validating screen-print color counts.",
  },
];

export const RULE_TYPE_OPTIONS = [
  {
    id: "required_charge",
    label: "Required Charge",
    description: "Require a configurable charge when your trigger conditions are met.",
    templateKey: "required_charge",
  },
  {
    id: "quantity_match",
    label: "Quantity Match",
    description: "Check whether decoration quantities align with the parent product quantity.",
    templateKey: "quantity_match",
  },
  {
    id: "minimum_quantity_charge",
    label: "Minimum Quantity Charge",
    description: "Require a charge when quantity falls below a configured minimum.",
    templateKey: "underminimum_required_charge",
  },
  {
    id: "missing_net_client_price",
    label: "Missing Net / Client Price",
    description: "Warn when net or client pricing values are blank or treated as missing.",
    templateKey: "missing_net_price_or_cost",
  },
  {
    id: "proof_required",
    label: "Proof Required",
    description: "Warn when proof is required but no proof request is recorded.",
    templateKey: "required_proof",
  },
  {
    id: "stitch_threshold_pricing",
    label: "Stitch Threshold Pricing",
    description: "Warn when embroidery exceeds a stitch threshold without the expected extra pricing.",
    templateKey: "stitch_threshold",
  },
  {
    id: "square_inch_pricing",
    label: "Square Inch Pricing",
    description: "Validate DTF pricing against width x height and the expected square-inch rate.",
    templateKey: "dtf_square_inch_pricing",
  },
];

export const SEVERITY_OPTIONS = [
  { value: "warning", label: "Warning" },
  { value: "fail", label: "Fail" },
];

export const SCOPE_OPTIONS = [
  { value: "global", label: "Global" },
  { value: "decorator", label: "Decorator" },
  { value: "instance", label: "Instance" },
  { value: "instance_decorator", label: "Instance + Decorator" },
];

export const REQUIRED_CHARGE_TRIGGER_OPTIONS = [
  { value: "always", label: "Always" },
  { value: "product_field_contains", label: "Product Field Contains" },
  { value: "decoration_field_equals", label: "Decoration Field Equals" },
  { value: "production_input_equals", label: "Production Input Equals" },
];

export const QUANTITY_TOLERANCE_OPTIONS = [
  { value: "exact", label: "Exact" },
  { value: "decoration_can_exceed", label: "Decoration Can Exceed" },
];

export const DTF_PRICE_FIELD_OPTIONS = [
  { value: "clientPrice", label: "Client Price" },
  { value: "netCost", label: "Net Cost" },
  { value: "retail", label: "Retail" },
  { value: "total", label: "Total" },
];

export const parseIntegerOrNull = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  if (!Number.isInteger(parsed)) return null;
  return parsed;
};

export const isDecoratorScoped = (scopeType) =>
  scopeType === "decorator" || scopeType === "instance_decorator";

export const isInstanceScoped = (scopeType) =>
  scopeType === "instance" || scopeType === "instance_decorator";

const buildBaseState = ({ decoratorId, instanceId, templateKey = "required_charge" } = {}) => ({
  id: null,
  name: "",
  templateKey,
  scopeType: instanceId ? "instance_decorator" : "decorator",
  instanceId: instanceId ? String(instanceId) : "",
  decoratorId: decoratorId ? String(decoratorId) : "",
  appliesToMethod: "",
  severity: "warning",
  active: true,
  triggerType: "always",
  triggerField: "",
  triggerValue: "",
  requiredChargeId: "",
  requiredChargeName: "",
  quantityComparison: "product_quantity_vs_decoration_quantity",
  quantityTolerance: "exact",
  behaviorChargeId: "",
  behaviorChargeName: "",
  fixedQuantityOne: false,
  runChargeMatchesProductQuantity: false,
  includeInPrice: false,
  includeInPriceEnabled: false,
  displayToClient: false,
  displayToClientEnabled: false,
  subtractFromMargin: false,
  subtractFromMarginEnabled: false,
  minimumQuantity: "24",
  requireNetCost: true,
  requireClientPrice: false,
  missingCostChargeId: "",
  missingCostChargeName: "",
  missingCostZeroIsMissing: true,
  proofRequestedField: "productionInputs.proofRequested",
  stitchThreshold: "10000",
  stitchRequiredChargeId: "",
  stitchRequiredChargeName: "Extra Stitch Charge",
  stitchMinimumNetCost: "",
  dtfMethod: "DTF",
  dtfWidthField: "meta.widthInches",
  dtfHeightField: "meta.heightInches",
  dtfExpectedRate: "",
  dtfPriceField: "clientPrice",
  dtfChargeId: "",
  dtfChargeName: "DTF",
  draftNotice: "",
});

export const buildDefaultFormState = ({ decoratorId, instanceId, templateKey = "required_charge" } = {}) =>
  buildBaseState({ decoratorId, instanceId, templateKey });

export const buildFormStateFromRule = (rule, { decoratorId, instanceId } = {}) => {
  const config = rule?.configJson || {};
  const trigger = config.trigger || {};

  return {
    ...buildBaseState({
      decoratorId,
      instanceId,
      templateKey: rule?.templateKey || "required_charge",
    }),
    id: rule?.id || null,
    name: rule?.name || "",
    templateKey: rule?.templateKey || "required_charge",
    scopeType: rule?.scopeType || (instanceId ? "instance_decorator" : "decorator"),
    instanceId:
      rule?.instanceId !== null && rule?.instanceId !== undefined
        ? String(rule.instanceId)
        : instanceId
          ? String(instanceId)
          : "",
    decoratorId:
      rule?.decoratorId !== null && rule?.decoratorId !== undefined
        ? String(rule.decoratorId)
        : decoratorId
          ? String(decoratorId)
          : "",
    appliesToMethod: rule?.appliesToMethod || "",
    severity: rule?.severity || "warning",
    active: rule?.active !== false,
    triggerType: trigger.type || "always",
    triggerField: trigger.field || "",
    triggerValue: trigger.value || "",
    requiredChargeId: config.requiredChargeId || "",
    requiredChargeName: config.requiredChargeName || "",
    quantityComparison:
      config.compare || "product_quantity_vs_decoration_quantity",
    quantityTolerance:
      config.tolerance ||
      (config.allowDecorationExceed ? "decoration_can_exceed" : "exact"),
    behaviorChargeId: config.chargeId || "",
    behaviorChargeName: config.chargeName || "",
    fixedQuantityOne: Boolean(config.fixedQuantityOne),
    runChargeMatchesProductQuantity: Boolean(
      config.runChargeMatchesProductQuantity
    ),
    includeInPriceEnabled: config.includeInPrice !== undefined,
    includeInPrice: Boolean(config.includeInPrice),
    displayToClientEnabled: config.displayToClient !== undefined,
    displayToClient: Boolean(config.displayToClient),
    subtractFromMarginEnabled: config.subtractFromMargin !== undefined,
    subtractFromMargin: Boolean(config.subtractFromMargin),
    minimumQuantity:
      config.minimumQuantity !== undefined && config.minimumQuantity !== null
        ? String(config.minimumQuantity)
        : "24",
    requireNetCost: config.requireNetCost !== false,
    requireClientPrice: Boolean(config.requireClientPrice),
    missingCostChargeId: config.chargeId || "",
    missingCostChargeName: config.chargeName || "",
    missingCostZeroIsMissing: config.zeroIsMissing !== false,
    proofRequestedField: config.proofRequestedField || "productionInputs.proofRequested",
    stitchThreshold:
      config.stitchThreshold !== undefined && config.stitchThreshold !== null
        ? String(config.stitchThreshold)
        : "10000",
    stitchRequiredChargeId: config.requiredChargeId || "",
    stitchRequiredChargeName: config.requiredChargeName || "",
    stitchMinimumNetCost:
      config.minimumNetCost !== undefined && config.minimumNetCost !== null
        ? String(config.minimumNetCost)
        : "",
    dtfMethod: config.method || "DTF",
    dtfWidthField: config.widthField || "meta.widthInches",
    dtfHeightField: config.heightField || "meta.heightInches",
    dtfExpectedRate:
      config.expectedRate !== undefined && config.expectedRate !== null
        ? String(config.expectedRate)
        : "",
    dtfPriceField: config.priceField || "clientPrice",
    dtfChargeId: config.chargeId || "",
    dtfChargeName: config.chargeName || "",
    draftNotice: config.draftNotice || "",
  };
};

export const serializeAuditRulePayload = (formState) => {
  const instanceId = isInstanceScoped(formState.scopeType)
    ? parseIntegerOrNull(formState.instanceId)
    : null;
  const decoratorId = isDecoratorScoped(formState.scopeType)
    ? parseIntegerOrNull(formState.decoratorId)
    : null;

  if (isInstanceScoped(formState.scopeType) && instanceId === null) {
    throw new Error("A numeric instanceId is required for this scope.");
  }

  if (isDecoratorScoped(formState.scopeType) && decoratorId === null) {
    throw new Error("A decorator is required for this scope.");
  }

  const payload = {
    name: formState.name.trim(),
    templateKey: formState.templateKey,
    scopeType: formState.scopeType,
    instanceId,
    decoratorId,
    appliesToMethod: formState.appliesToMethod.trim() || null,
    severity: formState.severity,
    active: Boolean(formState.active),
    configJson: {},
  };

  if (!payload.name) {
    throw new Error("Rule name is required.");
  }

  if (formState.templateKey === "required_charge") {
    payload.configJson = {
      trigger: {
        type: formState.triggerType,
        ...(formState.triggerType !== "always" && formState.triggerField.trim()
          ? { field: formState.triggerField.trim() }
          : {}),
        ...(formState.triggerType !== "always" && formState.triggerValue.trim()
          ? { value: formState.triggerValue.trim() }
          : {}),
      },
      requiredChargeId: formState.requiredChargeId.trim() || null,
      requiredChargeName: formState.requiredChargeName.trim() || null,
    };
  }

  if (formState.templateKey === "quantity_match") {
    payload.configJson = {
      compare: "product_quantity_vs_decoration_quantity",
      tolerance: formState.quantityTolerance,
      allowDecorationExceed:
        formState.quantityTolerance === "decoration_can_exceed",
    };
  }

  if (formState.templateKey === "charge_behavior") {
    payload.configJson = {
      chargeId: formState.behaviorChargeId.trim() || null,
      chargeName: formState.behaviorChargeName.trim() || null,
      fixedQuantityOne: Boolean(formState.fixedQuantityOne),
      runChargeMatchesProductQuantity: Boolean(
        formState.runChargeMatchesProductQuantity
      ),
      ...(formState.includeInPriceEnabled
        ? { includeInPrice: Boolean(formState.includeInPrice) }
        : {}),
      ...(formState.displayToClientEnabled
        ? { displayToClient: Boolean(formState.displayToClient) }
        : {}),
      ...(formState.subtractFromMarginEnabled
        ? { subtractFromMargin: Boolean(formState.subtractFromMargin) }
        : {}),
    };
  }

  if (formState.templateKey === "underminimum_required_charge") {
    payload.configJson = {
      minimumQuantity: Number(formState.minimumQuantity || 0) || 0,
      requiredChargeId: formState.requiredChargeId.trim() || null,
      requiredChargeName: formState.requiredChargeName.trim() || null,
    };
  }

  if (formState.templateKey === "missing_net_price_or_cost") {
    payload.configJson = {
      chargeId: formState.missingCostChargeId.trim() || null,
      chargeName: formState.missingCostChargeName.trim() || null,
      requireNetCost: Boolean(formState.requireNetCost),
      requireClientPrice: Boolean(formState.requireClientPrice),
      zeroIsMissing: Boolean(formState.missingCostZeroIsMissing),
    };
  }

  if (formState.templateKey === "required_proof") {
    payload.configJson = {
      proofRequestedField: formState.proofRequestedField.trim() || "productionInputs.proofRequested",
    };
  }

  if (formState.templateKey === "stitch_threshold") {
    payload.configJson = {
      stitchThreshold: Number(formState.stitchThreshold || 0) || 0,
      requiredChargeId: formState.stitchRequiredChargeId.trim() || null,
      requiredChargeName: formState.stitchRequiredChargeName.trim() || null,
      minimumNetCost:
        formState.stitchMinimumNetCost === ""
          ? null
          : Number(formState.stitchMinimumNetCost),
    };
  }

  if (formState.templateKey === "dtf_square_inch_pricing") {
    payload.configJson = {
      method: formState.dtfMethod.trim() || "DTF",
      widthField: formState.dtfWidthField.trim() || "meta.widthInches",
      heightField: formState.dtfHeightField.trim() || "meta.heightInches",
      expectedRate:
        formState.dtfExpectedRate === ""
          ? null
          : Number(formState.dtfExpectedRate),
      priceField: formState.dtfPriceField || "clientPrice",
      chargeId: formState.dtfChargeId.trim() || null,
      chargeName: formState.dtfChargeName.trim() || null,
      draftNotice: formState.draftNotice.trim() || null,
    };
  }

  return payload;
};

const buildQuickTemplatePatch = (templateId) => {
  if (templateId === "required_charge") {
    return {
      name: "Required Charge Rule",
      templateKey: "required_charge",
      severity: "warning",
      triggerType: "always",
      requiredChargeName: "",
    };
  }

  if (templateId === "quantity_match") {
    return {
      name: "Quantity Match Rule",
      templateKey: "quantity_match",
      severity: "warning",
      quantityTolerance: "exact",
    };
  }

  if (templateId === "minimum_quantity_charge") {
    return {
      name: "Minimum Quantity Charge Rule",
      templateKey: "underminimum_required_charge",
      severity: "warning",
      minimumQuantity: "24",
      requiredChargeName: "",
    };
  }

  if (templateId === "missing_net_client_price") {
    return {
      name: "Missing Net / Client Price Rule",
      templateKey: "missing_net_price_or_cost",
      severity: "warning",
      requireNetCost: true,
      requireClientPrice: false,
      missingCostZeroIsMissing: true,
    };
  }

  if (templateId === "proof_required") {
    return {
      name: "Proof Required Rule",
      templateKey: "required_proof",
      severity: "warning",
      proofRequestedField: "productionInputs.proofRequested",
    };
  }

  if (templateId === "stitch_threshold_pricing") {
    return {
      name: "Stitch Threshold Pricing Rule",
      templateKey: "stitch_threshold",
      severity: "warning",
      appliesToMethod: "Embroidery",
      stitchThreshold: "10000",
      stitchRequiredChargeName: "",
    };
  }

  if (templateId === "square_inch_pricing") {
    return {
      name: "Square Inch Pricing Rule",
      templateKey: "dtf_square_inch_pricing",
      severity: "warning",
      appliesToMethod: "DTF",
      dtfMethod: "DTF",
      dtfWidthField: "meta.widthInches",
      dtfHeightField: "meta.heightInches",
      dtfChargeName: "DTF",
      dtfPriceField: "clientPrice",
      draftNotice: "Set the expected square-inch rate before saving this rule.",
    };
  }

  return null;
};

export const buildQuickTemplateDraft = ({
  templateId,
  decoratorId,
  instanceId,
} = {}) => {
  const patch = buildQuickTemplatePatch(templateId);
  if (!patch) {
    throw new Error(`Unknown quick template '${templateId}'.`);
  }

  return {
    ...buildBaseState({
      decoratorId,
      instanceId,
      templateKey: patch.templateKey,
    }),
    ...patch,
  };
};
